import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ContentService } from '@app/_components/content';

export interface MediaUploadItem {
    id: string;
    file: File;
    localUrl: string;
    remoteUrl: string | null;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    mediaType: 'image' | 'video';
    status: 'pending' | 'uploading' | 'done' | 'error';
    progressPct: number;
    error: string | null;
}

function genId(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

@Injectable({ providedIn: 'root' })
export class MediaUploadService {

    private queue$ = new BehaviorSubject<MediaUploadItem[]>([]);

    constructor(private contentService: ContentService) {}

    getQueue(): Observable<MediaUploadItem[]> {
        return this.queue$.asObservable();
    }

    hasActiveUploads(): boolean {
        return this.queue$.value.some(i => i.status === 'pending' || i.status === 'uploading');
    }

    upload(file: File): Observable<MediaUploadItem> {
        const subject = new Subject<MediaUploadItem>();
        const id = genId();
        const localUrl = URL.createObjectURL(file);
        const mimeType = file.type;
        const mediaType: 'image' | 'video' = mimeType.startsWith('video/') ? 'video' : 'image';
        const fileName = `${id}-${file.name}`;

        const item: MediaUploadItem = {
            id,
            file,
            localUrl,
            remoteUrl: null,
            fileName,
            mimeType,
            sizeBytes: file.size,
            mediaType,
            status: 'pending',
            progressPct: 0,
            error: null
        };

        this.pushToQueue(item);
        setTimeout(() => this.doUpload(item, subject), 0);
        return subject.asObservable();
    }

    uploadAll(files: File[]): Observable<MediaUploadItem[]> {
        const subject = new Subject<MediaUploadItem[]>();
        const results: MediaUploadItem[] = [];
        let remaining = files.length;
        if (remaining === 0) {
            setTimeout(() => { subject.next([]); subject.complete(); }, 0);
            return subject.asObservable();
        }
        files.forEach(file => {
            this.upload(file).subscribe({
                next: item => {
                    if (item.status === 'done' || item.status === 'error') {
                        results.push(item);
                        remaining--;
                        subject.next([...results]);
                        if (remaining === 0) subject.complete();
                    }
                }
            });
        });
        return subject.asObservable();
    }

    retry(itemId: string): Observable<MediaUploadItem> {
        const existing = this.queue$.value.find(i => i.id === itemId);
        if (!existing) return new Subject<MediaUploadItem>().asObservable();
        this.updateItem(itemId, { status: 'pending', progressPct: 0, error: null });
        const subject = new Subject<MediaUploadItem>();
        const updated = this.queue$.value.find(i => i.id === itemId);
        setTimeout(() => this.doUpload(updated, subject), 0);
        return subject.asObservable();
    }

    remove(itemId: string): void {
        const item = this.queue$.value.find(i => i.id === itemId);
        if (item) URL.revokeObjectURL(item.localUrl);
        this.queue$.next(this.queue$.value.filter(i => i.id !== itemId));
    }

    waitForAll(): Promise<MediaUploadItem[]> {
        return new Promise(resolve => {
            const check = () => {
                const q = this.queue$.value;
                if (q.every(i => i.status === 'done' || i.status === 'error')) {
                    resolve(q);
                    return;
                }
                const sub = this.queue$.subscribe(items => {
                    if (items.every(i => i.status === 'done' || i.status === 'error')) {
                        sub.unsubscribe();
                        resolve(items);
                    }
                });
            };
            check();
        });
    }

    clearQueue(): void {
        this.queue$.value.forEach(i => URL.revokeObjectURL(i.localUrl));
        this.queue$.next([]);
    }

    private pushToQueue(item: MediaUploadItem): void {
        this.queue$.next([...this.queue$.value, item]);
    }

    private updateItem(id: string, patch: Partial<MediaUploadItem>): void {
        this.queue$.next(this.queue$.value.map(i => i.id === id ? { ...i, ...patch } : i));
    }

    private doUpload(item: MediaUploadItem, subject: Subject<MediaUploadItem>): void {
        this.updateItem(item.id, { status: 'uploading' });
        this.emit(subject, item.id);

        this.contentService.getUploadPresignedUrl(item.fileName).subscribe({
            next: (res: any) => {
                // The backend wraps the payload as { statusCode, message, data: { document: {...} } }.
                // DocumentAPI exposes presignedUrl but does NOT have a publicUrl field — the public URL
                // is the presigned URL with the signing query string stripped (the bucket name
                // "img.kbmhawaii.com" is a CNAME to the S3 bucket, so the host is already public).
                const doc = res?.data?.document ?? res?.document ?? res?.data ?? res ?? {};
                const presignedUrl: string = doc.presignedUrl;
                const publicUrl: string = doc.publicUrl ?? doc.fileUrl
                    ?? (presignedUrl ? presignedUrl.split('?')[0] : '');
                if (!presignedUrl) {
                    this.updateItem(item.id, { status: 'error', error: 'Server returned no upload URL' });
                    this.emit(subject, item.id);
                    subject.complete();
                    return;
                }
                this.xhrPut(item, presignedUrl, publicUrl, subject);
            },
            error: (err: any) => {
                // Prefer the server-provided message (e.g., "Failed to generate S3 presigned
                // upload URL. Verify AWS_S3_TASK_ACCESS_KEY ...") over the generic HttpErrorResponse text.
                const serverMsg = err?.error?.message;
                const msg = serverMsg || err?.message || 'Failed to get upload URL';
                this.updateItem(item.id, { status: 'error', error: msg });
                this.emit(subject, item.id);
                subject.complete();
            }
        });
    }

    private xhrPut(item: MediaUploadItem, presignedUrl: string, publicUrl: string, subject: Subject<MediaUploadItem>): void {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl, true);
        xhr.setRequestHeader('Content-Type', item.mimeType);

        xhr.upload.onprogress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
                const pct = Math.round((e.loaded / e.total) * 100);
                this.updateItem(item.id, { progressPct: pct });
                this.emit(subject, item.id);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                this.updateItem(item.id, { status: 'done', remoteUrl: publicUrl, progressPct: 100 });
                this.emit(subject, item.id);
                subject.complete();
            } else {
                const msg = `Upload failed (HTTP ${xhr.status})`;
                this.updateItem(item.id, { status: 'error', error: msg });
                this.emit(subject, item.id);
                subject.complete();
            }
        };

        xhr.onerror = () => {
            this.updateItem(item.id, { status: 'error', error: 'Network error during upload' });
            this.emit(subject, item.id);
            subject.complete();
        };

        xhr.send(item.file);
    }

    private emit(subject: Subject<MediaUploadItem>, id: string): void {
        const item = this.queue$.value.find(i => i.id === id);
        if (item) subject.next(item);
    }
}
